import { Injectable } from '@angular/core';
import { CanvasObject, Point, RGBA, Size } from '../models/canvas-object.model';
import { CanvasService } from './canvas.service';
import jsPDF from 'jspdf';
import 'svg2pdf.js';

@Injectable({
    providedIn: 'root'
})
export class ExportImportService {
    private warnedNoEditableText = false;

    constructor(private canvasService: CanvasService) {
    }

    exportToSVG(): string {
        const canvasSize = this.canvasService.canvasSize();
        if (this.hasBackSide()) {
            return this.buildTwoSidedSVG(canvasSize.width, canvasSize.height);
        }
        const bounds = { x: 0, y: 0, width: canvasSize.width, height: canvasSize.height };
        return this.buildSVG(bounds, canvasSize.width, canvasSize.height);
    }

    exportToSVGForPDF(): string {
        return this.exportToSVG();
    }

    exportToSVGForPrint(): string {
        const printConfig = this.getVisitingCardPrintConfig();
        if (!printConfig) {
            return this.exportToSVGForPDF();
        }
        if (this.hasBackSide()) {
            return this.exportToSVG();
        }
        const canvasSize = this.canvasService.canvasSize();
        const bounds = { x: 0, y: 0, width: canvasSize.width, height: canvasSize.height };
        return this.buildSVG(bounds, `${printConfig.widthMm}mm`, `${printConfig.heightMm}mm`);
    }

    async exportToPDF(): Promise<void> {
        const canvasSize = this.canvasService.canvasSize();
        const svgContent = this.exportToSVGForPDF();

        if (this.hasBackSide()) {
            const totalHeight = this.getTwoSidedHeight(canvasSize.height);
            const pdf = new jsPDF({
                orientation: canvasSize.width > totalHeight ? 'landscape' : 'portrait',
                unit: 'pt',
                format: [canvasSize.width, totalHeight]
            });

            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');

            if (svgElement) {
                await (pdf as any).svg(svgElement, {
                    x: 0,
                    y: 0,
                    width: canvasSize.width,
                    height: totalHeight
                });
            }

            pdf.save('canvas-export.pdf');
            return;
        }

        const printConfig = this.getVisitingCardPrintConfig();
        if (printConfig) {
            const widthPt = this.mmToPt(printConfig.widthMm);
            const heightPt = this.mmToPt(printConfig.heightMm);
            const pdf = new jsPDF({
                orientation: widthPt > heightPt ? 'landscape' : 'portrait',
                unit: 'pt',
                format: [widthPt, heightPt]
            });

            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');

            if (svgElement) {
                await (pdf as any).svg(svgElement, {
                    x: 0,
                    y: 0,
                    width: widthPt,
                    height: heightPt
                });
            }

            pdf.save('canvas-export.pdf');
            return;
        }

        // Create PDF with proper page dimensions
        const pdf = new jsPDF({
            orientation: canvasSize.width > canvasSize.height ? 'landscape' : 'portrait',
            unit: 'pt',
            format: [canvasSize.width, canvasSize.height]
        });

        // Parse SVG and add to PDF using svg2pdf
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
            // Use svg2pdf to render SVG directly to PDF
            await (pdf as any).svg(svgElement, {
                x: 0,
                y: 0,
                width: canvasSize.width,
                height: canvasSize.height
            });
        }

        // Download the PDF
        pdf.save('canvas-export.pdf');
    }

    exportToPNG(): void {
        // Export canvas as PNG image
        const printConfig = this.getVisitingCardPrintConfig();
        const canvasSize = this.canvasService.canvasSize();
        const isTwoSided = this.hasBackSide();
        const outputWidth = printConfig && !isTwoSided
            ? Math.round(canvasSize.width * printConfig.scale)
            : canvasSize.width;
        const outputHeight = printConfig && !isTwoSided
            ? Math.round(canvasSize.height * printConfig.scale)
            : (isTwoSided ? this.getTwoSidedHeight(canvasSize.height) : canvasSize.height);

        const svgContent = isTwoSided
            ? this.buildTwoSidedSVG(canvasSize.width, canvasSize.height)
            : this.buildSVG(
                { x: 0, y: 0, width: canvasSize.width, height: canvasSize.height },
                outputWidth,
                outputHeight
            );
        const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
        const svgUrl = URL.createObjectURL(svgBlob);

        const pngImg = new Image();
        pngImg.onload = () => {
            const pngCanvas = document.createElement('canvas');
            pngCanvas.width = outputWidth;
            pngCanvas.height = outputHeight;
            const pngCtx = pngCanvas.getContext('2d')!;
            pngCtx.drawImage(pngImg, 0, 0, outputWidth, outputHeight);

            pngCanvas.toBlob(pngBlob => {
                if (pngBlob) {
                    this.downloadFile(pngBlob, 'canvas-export.png');
                }
                URL.revokeObjectURL(svgUrl);
            });
        };
        pngImg.src = svgUrl;
    }

    downloadSVG(): void {
        const svg = this.getVisitingCardPrintConfig() ? this.exportToSVGForPrint() : this.exportToSVG();
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        this.downloadFile(blob, 'canvas-export.svg');
    }

    downloadJSON(): void {
        const objects = this.canvasService.objects();
        const data = {
            version: '1.0',
            canvasSizeType: this.canvasService.canvasSizeTypeValue(),
            canvasCustomSize: this.canvasService.canvasCustomSize(),
            objects: objects.map(obj => this.serializeObject(obj))
        };
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        this.downloadFile(blob, 'canvas-export.json');
    }

    /**
     * Return the export JSON as a string (not downloaded).
     */
    getExportJSON(): string {
        const objects = this.canvasService.objects();
        const data = {
            version: '1.0',
            objects: objects.map(obj => this.serializeObject(obj))
        };
        return JSON.stringify(data, null, 2);
    }

    importFromJSON(json: string): void {
        try {
            const data = JSON.parse(json);
            if (data.canvasSizeType) {
                if (data.canvasSizeType === 'custom' && data.canvasCustomSize) {
                    this.canvasService.setCanvasCustomSize(data.canvasCustomSize);
                } else {
                    this.canvasService.setCanvasSize(data.canvasSizeType);
                }
            }
            if (data.objects && Array.isArray(data.objects)) {
                this.canvasService.clear();
                data.objects.forEach((obj: any) => {
                    this.canvasService.addObject(obj);
                });
            }
        } catch (error) {
            console.error('Failed to import JSON:', error);
        }
    }

    async importImage(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const src = e.target?.result as string;
                const img = new Image();
                img.onload = () => {
                    const id = this.canvasService.addObject({
                        type: 'image',
                        src,
                        originalSize: { width: img.width, height: img.height },
                        size: { width: img.width, height: img.height },
                        position: { x: 100, y: 100 },
                        cornerRadius: 0
                    } as any);
                    resolve(id);
                };
                img.onerror = reject;
                img.src = src;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async importSVG(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const svgContent = e.target?.result as string;

                // Parse SVG to get dimensions
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
                const svgElement = svgDoc.querySelector('svg');

                let width = 100;
                let height = 100;
                let viewBoxWidth = 0;
                let viewBoxHeight = 0;

                let extractedText: Array<{
                    x: number;
                    y: number;
                    content: string;
                    fontSize: number;
                    fontFamily: string;
                    fontWeight: string | number;
                    fontStyle: 'normal' | 'italic';
                    textAlign: 'left' | 'center' | 'right';
                    fill: RGBA;
                    opacity: number;
                    lineCount: number;
                    maxLineLength: number;
                }> = [];

                if (svgElement) {
                    extractedText = this.extractEditableText(svgElement);
                    if (extractedText.length === 0 && !this.warnedNoEditableText) {
                        this.warnedNoEditableText = true;
                        alert('This SVG has no editable text. Text may be outlined as shapes and cannot be selected.');
                    }
                }

                if (svgElement) {
                    const wAttr = svgElement.getAttribute('width');
                    const hAttr = svgElement.getAttribute('height');
                    const viewBox = svgElement.getAttribute('viewBox');

                    if (viewBox) {
                        const parts = viewBox.split(/\s+/).map(parseFloat).filter(n => !isNaN(n));
                        if (parts.length === 4) {
                            viewBoxWidth = parts[2];
                            viewBoxHeight = parts[3];
                        }
                    }

                    const parsedWidth = this.parseSvgLength(wAttr, viewBoxWidth);
                    const parsedHeight = this.parseSvgLength(hAttr, viewBoxHeight);
                    if (parsedWidth) width = parsedWidth;
                    if (parsedHeight) height = parsedHeight;

                    if (!parsedWidth && viewBoxWidth) width = viewBoxWidth;
                    if (!parsedHeight && viewBoxHeight) height = viewBoxHeight;

                    if (!wAttr || /%$/.test(wAttr)) {
                        svgElement.setAttribute('width', String(width));
                    }
                    if (!hAttr || /%$/.test(hAttr)) {
                        svgElement.setAttribute('height', String(height));
                    }
                }

                if (svgElement) {
                    if (!svgElement.getAttribute('viewBox')) {
                        svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
                    }
                    if (!svgElement.getAttribute('xmlns')) {
                        svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    }
                    if (!svgElement.getAttribute('xmlns:xlink')) {
                        svgElement.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                    }
                }

                // Extract layers from SVG
                const layers = this.extractSVGLayers(svgElement);

                // Use parsed outerHTML to ensure clean SVG content
                const svgContent_ = svgElement ? svgElement.outerHTML : svgContent;

                const canvasSize = this.canvasService.canvasSize();
                const fitScale = Math.min(
                    1,
                    canvasSize.width / Math.max(width, 1),
                    canvasSize.height / Math.max(height, 1)
                );
                const fittedWidth = Math.max(1, Math.round(width * fitScale));
                const fittedHeight = Math.max(1, Math.round(height * fitScale));
                const position = {
                    x: Math.max(0, Math.round((canvasSize.width - fittedWidth) / 2)),
                    y: Math.max(0, Math.round((canvasSize.height - fittedHeight) / 2))
                };

                const svgBlob = new Blob([svgContent_], { type: 'image/svg+xml' });
                const svgObjectUrl = URL.createObjectURL(svgBlob);
                const id = this.canvasService.addObject({
                    type: 'svg',
                    svgContent: svgContent_,
                    src: svgObjectUrl,
                    originalSize: { width, height },
                    size: { width: fittedWidth, height: fittedHeight },
                    position,
                    layers,
                    hasEditableText: extractedText.length > 0
                } as any);

                if (extractedText.length > 0) {
                    const baseZ = this.canvasService.getObject(id)?.zIndex ?? this.canvasService.objects().length;
                    const scaleX = fittedWidth / Math.max(width, 1);
                    const scaleY = fittedHeight / Math.max(height, 1);
                    const lineHeight = 1.2;

                    extractedText.forEach((textNode, index) => {
                        const fontSize = Math.max(1, textNode.fontSize * scaleY);
                        const lineWidth = textNode.maxLineLength * fontSize * 0.6;
                        const textWidth = Math.max(1, lineWidth);
                        const textHeight = Math.max(1, textNode.lineCount * fontSize * lineHeight);
                        const baselineOffset = textNode.fontSize * 0.8;
                        const x = position.x + (textNode.x * scaleX);
                        const y = position.y + ((textNode.y - baselineOffset) * scaleY);
                        const fill = { ...textNode.fill, a: textNode.fill.a * textNode.opacity };

                        this.canvasService.addObject({
                            type: 'text',
                            content: textNode.content,
                            position: { x, y },
                            size: { width: textWidth, height: textHeight },
                            fill,
                            textStyle: {
                                fontFamily: textNode.fontFamily,
                                fontSize,
                                fontWeight: textNode.fontWeight,
                                fontStyle: textNode.fontStyle,
                                textAlign: textNode.textAlign,
                                textDecoration: 'none',
                                lineHeight,
                                letterSpacing: 0
                            },
                            opacity: 1,
                            zIndex: baseZ + 1 + index
                        } as any);
                    });
                }
                resolve(id);
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    }

    private extractEditableText(svgElement: SVGSVGElement): Array<{
        x: number;
        y: number;
        content: string;
        fontSize: number;
        fontFamily: string;
        fontWeight: string | number;
        fontStyle: 'normal' | 'italic';
        textAlign: 'left' | 'center' | 'right';
        fill: RGBA;
        opacity: number;
        lineCount: number;
        maxLineLength: number;
    }> {
        const results: Array<{
            x: number;
            y: number;
            content: string;
            fontSize: number;
            fontFamily: string;
            fontWeight: string | number;
            fontStyle: 'normal' | 'italic';
            textAlign: 'left' | 'center' | 'right';
            fill: RGBA;
            opacity: number;
            lineCount: number;
            maxLineLength: number;
        }> = [];

        const textNodes = Array.from(svgElement.querySelectorAll('text'));
        textNodes.forEach(textEl => {
            const styleMap = this.parseInlineStyle(textEl.getAttribute('style'));
            const x = this.parseNumberList(textEl.getAttribute('x') ?? styleMap['x'] ?? '0')[0] ?? 0;
            const y = this.parseNumberList(textEl.getAttribute('y') ?? styleMap['y'] ?? '0')[0] ?? 0;
            const fontSize = this.parseNumber(styleMap['font-size'] ?? textEl.getAttribute('font-size') ?? '16', 16);
            const fontFamily = (styleMap['font-family'] ?? textEl.getAttribute('font-family') ?? 'Arial').toString();
            const fontWeight = (styleMap['font-weight'] ?? textEl.getAttribute('font-weight') ?? 'normal').toString();
            const fontStyle = (styleMap['font-style'] ?? textEl.getAttribute('font-style') ?? 'normal') === 'italic' ? 'italic' : 'normal';
            const textAnchor = (styleMap['text-anchor'] ?? textEl.getAttribute('text-anchor') ?? 'start').toString();
            const fillValue = (styleMap['fill'] ?? textEl.getAttribute('fill') ?? styleMap['color'] ?? '#000000').toString();
            const opacity = this.parseNumber(styleMap['opacity'] ?? textEl.getAttribute('opacity') ?? '1', 1);

            const lines = this.extractTextLines(textEl);
            const content = lines.join('\n').trim();
            if (!content) {
                textEl.parentElement?.removeChild(textEl);
                return;
            }

            const maxLineLength = lines.reduce((max, line) => Math.max(max, line.length), 0);
            const textAlign = textAnchor === 'middle'
                ? 'center'
                : textAnchor === 'end'
                    ? 'right'
                    : 'left';

            results.push({
                x,
                y,
                content,
                fontSize,
                fontFamily,
                fontWeight,
                fontStyle,
                textAlign,
                fill: this.parseSvgColor(fillValue),
                opacity,
                lineCount: Math.max(1, lines.length),
                maxLineLength: Math.max(1, maxLineLength)
            });

            textEl.parentElement?.removeChild(textEl);
        });

        return results;
    }

    private extractTextLines(textEl: Element): string[] {
        const tspans = Array.from(textEl.querySelectorAll('tspan'));
        if (tspans.length === 0) {
            const raw = textEl.textContent ?? '';
            return [raw.trim()];
        }

        return tspans
            .map(tspan => (tspan.textContent ?? '').trim())
            .filter(line => line.length > 0);
    }

    private parseInlineStyle(styleText: string | null): Record<string, string> {
        if (!styleText) return {};
        return styleText
            .split(';')
            .map(part => part.trim())
            .filter(part => part.includes(':'))
            .reduce((acc, part) => {
                const [key, value] = part.split(':').map(s => s.trim());
                if (key && value) acc[key] = value;
                return acc;
            }, {} as Record<string, string>);
    }

    private parseNumber(value: string, fallback: number): number {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    private parseNumberList(value: string): number[] {
        return value
            .split(/[ ,]+/)
            .map(item => Number.parseFloat(item))
            .filter(num => Number.isFinite(num));
    }

    private parseSvgColor(value: string): RGBA {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'none') {
            return { r: 0, g: 0, b: 0, a: 1 };
        }

        const named: Record<string, RGBA> = {
            black: { r: 0, g: 0, b: 0, a: 1 },
            white: { r: 255, g: 255, b: 255, a: 1 },
            red: { r: 255, g: 0, b: 0, a: 1 },
            blue: { r: 0, g: 0, b: 255, a: 1 },
            green: { r: 0, g: 128, b: 0, a: 1 },
            gray: { r: 128, g: 128, b: 128, a: 1 },
            grey: { r: 128, g: 128, b: 128, a: 1 }
        };

        if (named[normalized]) return named[normalized];

        if (normalized.startsWith('#')) {
            const hex = normalized.slice(1);
            if (hex.length === 3) {
                const r = Number.parseInt(hex[0] + hex[0], 16);
                const g = Number.parseInt(hex[1] + hex[1], 16);
                const b = Number.parseInt(hex[2] + hex[2], 16);
                return { r, g, b, a: 1 };
            }
            if (hex.length === 6) {
                const r = Number.parseInt(hex.slice(0, 2), 16);
                const g = Number.parseInt(hex.slice(2, 4), 16);
                const b = Number.parseInt(hex.slice(4, 6), 16);
                return { r, g, b, a: 1 };
            }
        }

        const rgbMatch = normalized.match(/rgba?\(([^)]+)\)/);
        if (rgbMatch) {
            const parts = rgbMatch[1].split(',').map(item => item.trim());
            const r = this.parseNumber(parts[0], 0);
            const g = this.parseNumber(parts[1], 0);
            const b = this.parseNumber(parts[2], 0);
            const a = parts[3] !== undefined ? this.parseNumber(parts[3], 1) : 1;
            return { r, g, b, a };
        }

        return { r: 0, g: 0, b: 0, a: 1 };
    }

    private objectToSVG(obj: CanvasObject, bounds: { x: number; y: number; width: number; height: number }): string {
        let svg = '';
        const transform = this.getTransformString(obj);
        const fillStyle = obj.fill ? this.rgbaToString(obj.fill) : 'none';
        const strokeStyle = obj.stroke ? this.rgbaToString(obj.stroke.color) : 'none';
        const strokeWidth = obj.stroke?.width || 0;
        const opacity = obj.opacity;

        switch (obj.type) {
            case 'rectangle':
                svg += `<rect x="${obj.position.x}" y="${obj.position.y}" width="${obj.size.width}" height="${obj.size.height}" `;
                if (obj.cornerRadius) {
                    svg += `rx="${obj.cornerRadius}" ry="${obj.cornerRadius}" `;
                }
                svg += `fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;

            case 'circle':
                const cx = obj.position.x + obj.size.width / 2;
                const cy = obj.position.y + obj.size.height / 2;
                svg += `<circle cx="${cx}" cy="${cy}" r="${obj.radius}" fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;

            case 'ellipse':
                const ecx = obj.position.x + obj.size.width / 2;
                const ecy = obj.position.y + obj.size.height / 2;
                svg += `<ellipse cx="${ecx}" cy="${ecy}" rx="${obj.size.width / 2}" ry="${obj.size.height / 2}" fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;

            case 'triangle':
            case 'diamond':
            case 'pentagon':
            case 'hexagon':
            case 'star':
            case 'arrow': {
                const points = this.getShapePoints(obj.type, obj.position, obj.size);
                const pointsAttr = points.map(p => `${p.x},${p.y}`).join(' ');
                svg += `<polygon points="${pointsAttr}" fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;
            }

            case 'heart': {
                const path = this.getHeartPath(obj.position, obj.size);
                svg += `<path d="${path}" fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;
            }

            case 'check': {
                const path = this.getCheckPath(obj.position, obj.size);
                svg += `<path d="${path}" fill="none" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;
            }

            case 'cloud': {
                const path = this.getCloudPath(obj.position, obj.size);
                svg += `<path d="${path}" fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                break;
            }

            case 'line':
            case 'polygon':
                const points = obj.points.map(p => `${p.x},${p.y}`).join(' ');
                if (obj.type === 'polygon' && obj.closed) {
                    svg += `<polygon points="${points}" fill="${fillStyle}" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                } else {
                    svg += `<polyline points="${points}" fill="none" stroke="${strokeStyle}" stroke-width="${strokeWidth}" opacity="${opacity}" transform="${transform}" />`;
                }
                break;

            case 'text':
                const baselineOffset = obj.textStyle.fontSize * 0.8;
                const textY = obj.position.y + baselineOffset;
                svg += `<text x="${obj.position.x}" y="${textY}" `;
                svg += `font-family="${obj.textStyle.fontFamily}" font-size="${obj.textStyle.fontSize}" `;
                svg += `font-weight="${obj.textStyle.fontWeight}" font-style="${obj.textStyle.fontStyle}" `;
                svg += `letter-spacing="${obj.textStyle.letterSpacing ?? 0}" `;
                svg += `text-anchor="${obj.textStyle.textAlign === 'center' ? 'middle' : obj.textStyle.textAlign === 'right' ? 'end' : 'start'}" `;
                svg += `xml:space="preserve" `;
                svg += `fill="${fillStyle}" opacity="${opacity}" transform="${transform}">`;

                const lines = obj.content.split('\n');
                const lineHeight = obj.textStyle.fontSize * obj.textStyle.lineHeight;
                lines.forEach((line, index) => {
                    const dy = index === 0 ? 0 : lineHeight;
                    svg += `<tspan x="${obj.position.x}" dy="${dy}">${this.escapeXml(line)}</tspan>`;
                });

                svg += '</text>';
                break;

            case 'image':
                svg += `<image x="${obj.position.x}" y="${obj.position.y}" width="${obj.size.width}" height="${obj.size.height}" `;
                svg += `href="${obj.src}" opacity="${opacity}" transform="${transform}" />`;
                break;

            case 'svg':
                // Embed the SVG content
                svg += `<g transform="translate(${obj.position.x},${obj.position.y}) ${transform}">`;
                svg += obj.svgContent;
                svg += '</g>';
                break;

            case 'group':
            case 'frame':
                svg += `<g transform="${transform}">`;
                // Would need to render children here
                svg += '</g>';
                break;
        }

        return svg;
    }

    private buildSVG(
        bounds: { x: number; y: number; width: number; height: number },
        width: number | string,
        height: number | string,
        objects?: CanvasObject[]
    ): string {
        const items = objects ?? this.canvasService.objects();
        let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}">`;

        const sorted = [...items].sort((a, b) => a.zIndex - b.zIndex);
        sorted.forEach(obj => {
            if (!obj.visible) return;
            svg += this.objectToSVG(obj, bounds);
        });

        svg += '</svg>';
        return svg;
    }

    private buildSVGContent(objects: CanvasObject[], bounds: { x: number; y: number; width: number; height: number }): string {
        const sorted = [...objects].sort((a, b) => a.zIndex - b.zIndex);
        let markup = '';
        sorted.forEach(obj => {
            if (!obj.visible) return;
            markup += this.objectToSVG(obj, bounds);
        });
        return markup;
    }

    private buildTwoSidedSVG(width: number, height: number): string {
        const gap = Math.round(height * 0.1);
        const totalHeight = this.getTwoSidedHeight(height);
        const bounds = { x: 0, y: 0, width, height };
        const front = this.getObjectsForSide('front');
        const back = this.getObjectsForSide('back');

        let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${totalHeight}" viewBox="0 0 ${width} ${totalHeight}">`;
        svg += `<g>${this.buildSVGContent(front, bounds)}</g>`;
        svg += `<g transform="translate(0 ${height + gap})">${this.buildSVGContent(back, bounds)}</g>`;
        svg += '</svg>';
        return svg;
    }

    private getObjectsForSide(side: 'front' | 'back'): CanvasObject[] {
        return this.canvasService.objects().filter(obj => (obj.side || 'front') === side);
    }

    private hasBackSide(): boolean {
        return this.canvasService.canvasSizeTypeValue() === 'visiting-card' && this.getObjectsForSide('back').length > 0;
    }

    private getTwoSidedHeight(height: number): number {
        const gap = Math.round(height * 0.1);
        return height * 2 + gap;
    }

    private getVisitingCardPrintConfig(): { widthMm: number; heightMm: number; scale: number } | null {
        if (this.canvasService.canvasSizeTypeValue() !== 'visiting-card') {
            return null;
        }
        return { widthMm: 90, heightMm: 54, scale: 300 / 96 };
    }

    private mmToPt(valueMm: number): number {
        return (valueMm * 72) / 25.4;
    }

    private getTransformString(obj: CanvasObject): string {
        const { rotation, scaleX, scaleY } = obj.transform;
        const cx = obj.position.x + obj.size.width / 2;
        const cy = obj.position.y + obj.size.height / 2;

        let transform = '';
        if (rotation !== 0) {
            transform += `rotate(${rotation} ${cx} ${cy}) `;
        }
        if (scaleX !== 1 || scaleY !== 1) {
            transform += `scale(${scaleX} ${scaleY}) `;
        }
        return transform.trim();
    }

    private getShapePoints(type: string, position: Point, size: Size): Point[] {
        const centerX = position.x + size.width / 2;
        const centerY = position.y + size.height / 2;
        const radius = Math.min(size.width, size.height) / 2;

        switch (type) {
            case 'triangle':
                return [
                    { x: centerX, y: position.y },
                    { x: position.x + size.width, y: position.y + size.height },
                    { x: position.x, y: position.y + size.height }
                ];
            case 'diamond':
                return [
                    { x: centerX, y: position.y },
                    { x: position.x + size.width, y: centerY },
                    { x: centerX, y: position.y + size.height },
                    { x: position.x, y: centerY }
                ];
            case 'pentagon':
                return this.createRegularPolygonPoints(5, centerX, centerY, radius, -Math.PI / 2);
            case 'hexagon':
                return this.createRegularPolygonPoints(6, centerX, centerY, radius, -Math.PI / 2);
            case 'star':
                return this.createStarPoints(5, centerX, centerY, radius, radius * 0.5, -Math.PI / 2);
            case 'arrow':
                return [
                    { x: position.x, y: position.y + size.height * 0.3 },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.3 },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.05 },
                    { x: position.x + size.width, y: centerY },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.95 },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.7 },
                    { x: position.x, y: position.y + size.height * 0.7 }
                ];
            default:
                return this.createRegularPolygonPoints(4, centerX, centerY, radius, -Math.PI / 4);
        }
    }

    private createRegularPolygonPoints(sides: number, cx: number, cy: number, radius: number, rotation: number): Point[] {
        const points: Point[] = [];
        for (let i = 0; i < sides; i++) {
            const angle = rotation + (i * 2 * Math.PI) / sides;
            points.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
        }
        return points;
    }

    private createStarPoints(points: number, cx: number, cy: number, outerRadius: number, innerRadius: number, rotation: number): Point[] {
        const result: Point[] = [];
        const totalPoints = points * 2;
        for (let i = 0; i < totalPoints; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = rotation + (i * Math.PI) / points;
            result.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
        }
        return result;
    }

    private getHeartPath(position: Point, size: Size): string {
        const x = position.x;
        const y = position.y;
        const w = size.width;
        const h = size.height;

        const startX = x + w / 2;
        const startY = y + h;

        return `M ${startX} ${startY} ` +
            `C ${x + w * 0.9} ${y + h * 0.85} ${x + w} ${y + h * 0.55} ${x + w * 0.8} ${y + h * 0.3} ` +
            `C ${x + w * 0.65} ${y + h * 0.05} ${x + w * 0.5} ${y + h * 0.1} ${x + w / 2} ${y + h * 0.25} ` +
            `C ${x + w * 0.5} ${y + h * 0.1} ${x + w * 0.35} ${y + h * 0.05} ${x + w * 0.2} ${y + h * 0.3} ` +
            `C ${x} ${y + h * 0.55} ${x + w * 0.1} ${y + h * 0.85} ${startX} ${startY} Z`;
    }

    private getCheckPath(position: Point, size: Size): string {
        const x1 = position.x + size.width * 0.15;
        const y1 = position.y + size.height * 0.55;
        const x2 = position.x + size.width * 0.4;
        const y2 = position.y + size.height * 0.8;
        const x3 = position.x + size.width * 0.85;
        const y3 = position.y + size.height * 0.25;
        return `M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3}`;
    }

    private getCloudPath(position: Point, size: Size): string {
        const x = position.x;
        const y = position.y;
        const w = size.width;
        const h = size.height;
        return `M ${x + w * 0.2} ${y + h * 0.6} ` +
            `C ${x + w * 0.05} ${y + h * 0.6} ${x + w * 0.05} ${y + h * 0.35} ${x + w * 0.2} ${y + h * 0.35} ` +
            `C ${x + w * 0.25} ${y + h * 0.2} ${x + w * 0.45} ${y + h * 0.15} ${x + w * 0.55} ${y + h * 0.28} ` +
            `C ${x + w * 0.7} ${y + h * 0.2} ${x + w * 0.9} ${y + h * 0.3} ${x + w * 0.9} ${y + h * 0.48} ` +
            `C ${x + w * 0.98} ${y + h * 0.5} ${x + w * 0.98} ${y + h * 0.68} ${x + w * 0.85} ${y + h * 0.7} ` +
            `C ${x + w * 0.8} ${y + h * 0.85} ${x + w * 0.6} ${y + h * 0.9} ${x + w * 0.5} ${y + h * 0.8} ` +
            `C ${x + w * 0.35} ${y + h * 0.9} ${x + w * 0.2} ${y + h * 0.85} ${x + w * 0.2} ${y + h * 0.6} Z`;
    }

    private extractSVGLayers(svgElement: SVGSVGElement | null): any[] {
        if (!svgElement) return [];

        const layers: any[] = [];
        let layerIndex = 0;

        // Find all paths, circles, rects, polygons, etc. that have fill or stroke
        const elements = svgElement.querySelectorAll('path, circle, rect, ellipse, polygon, polyline, line');

        elements.forEach((el) => {
            const fill = el.getAttribute('fill');
            const stroke = el.getAttribute('stroke');
            const id = el.getAttribute('id') || `layer-${layerIndex++}`;

            // Skip elements with no fill or stroke
            if (!fill && !stroke) return;

            // Add unique id to element if it doesn't have one
            if (!el.getAttribute('id')) {
                el.setAttribute('id', id);
            }

            layers.push({
                id: el.getAttribute('id'),
                name: el.getAttribute('id') || el.tagName,
                fill: fill || undefined,
                stroke: stroke || undefined,
                originalFill: fill || undefined,
                originalStroke: stroke || undefined
            });
        });

        return layers;
    }

    private rgbaToString(rgba: RGBA): string {
        return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
    }

    private escapeXml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }

    private parseSvgLength(value: string | null, fallback: number): number | null {
        if (!value) return fallback || null;
        const trimmed = value.trim();
        if (trimmed.endsWith('%')) return fallback || null;

        const num = Number.parseFloat(trimmed);
        if (!Number.isFinite(num)) return fallback || null;

        const unit = trimmed.replace(String(num), '').trim().toLowerCase();
        if (!unit || unit === 'px') return num;
        if (unit === 'mm') return num * (96 / 25.4);
        if (unit === 'cm') return num * (96 / 2.54);
        if (unit === 'in') return num * 96;
        if (unit === 'pt') return num * (96 / 72);
        if (unit === 'pc') return num * 16;
        return num;
    }

    private calculateBounds(objects: CanvasObject[]): { x: number; y: number; width: number; height: number } {
        if (objects.length === 0) {
            return { x: 0, y: 0, width: 800, height: 600 };
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        objects.forEach(obj => {
            minX = Math.min(minX, obj.position.x);
            minY = Math.min(minY, obj.position.y);
            maxX = Math.max(maxX, obj.position.x + obj.size.width);
            maxY = Math.max(maxY, obj.position.y + obj.size.height);
        });

        // Add some padding
        const padding = 20;
        return {
            x: minX - padding,
            y: minY - padding,
            width: maxX - minX + padding * 2,
            height: maxY - minY + padding * 2
        };
    }

    private serializeObject(obj: CanvasObject): any {
        // Ensure SVG objects with layers are properly serialized
        const serialized = JSON.parse(JSON.stringify(obj));

        // Preserve SVG layers when serializing
        if ((obj as any).layers && Array.isArray((obj as any).layers)) {
            serialized.layers = (obj as any).layers.map((layer: any) => ({
                ...layer,
                fill: layer.fill,
                stroke: layer.stroke,
                originalFill: layer.originalFill,
                originalStroke: layer.originalStroke
            }));
        }

        return serialized;
    }

    private downloadFile(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }
}
